// Company Car (from [Reporting].CompanyCars with JOINs)
export interface CompanyCar {
    Id: number;
    CompanyCode: string;
    StaffID: string | null;
    DriverName: string | null;
    DriverShort: string | null;
    StaffNumber: string | null;
    DriverEmail: string | null;
    CarBrandId: number | null;
    BrandName: string | null;
    CarModel: string | null;
    LicensePlate: string | null;
    CarID: string | null;
    VIN: string | null;
    FixedAssetNo: string | null;
    MatriculationDate: string | null;
    EndOfUse: string | null;
    IsActive: boolean;
    FuelTypeId: number | null;
    FuelLabel: string | null;
    FuelCode: string | null;
    IsElectric: boolean;
    KwhPer100km: number | null;
    FuelLPer100km: number | null;
    Co2WltpKgPerKm: number | null;
    IsLeasing: boolean;
    LeasingCompanyId: number | null;
    LeasingCompanyName: string | null;
    LeasingDurationMonths: number | null;
    LeasingContractKm: number | null;
    LeasingExpiryDate: string | null;
    CurrentMileageKm: number | null;
    PurchasePrice: number | null;
    LeasingRate: number | null;
    Insurance: string | null;
    InsuranceCompanyId: number | null;
    InsuranceCompanyName: string | null;
    InsuranceType: string | null;
    InsuranceDeductible: number | null;
    Notes: string | null;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
    /** Vehicle key for documents (computed in the DB, migration 224). null = no usable plate. */
    PlateKey: string | null;
    /** Active documents on this vehicle — the same on every row of the same car. */
    DocumentCount: number;
    /** Earliest of the latest ValidUntil per document type, 'YYYY-MM-DD…' or null. */
    NextValidUntil: string | null;
}

// Vehicle documents (migration 224)
export type CarDocumentType = 'registration' | 'purchase' | 'leasing' | 'insurance' | 'other';

export const CAR_DOCUMENT_TYPES: { value: CarDocumentType; label: string }[] = [
    // Label only: the stored value stays 'registration' (CK_CompanyCarDocuments_Type, migration 224).
    { value: 'registration', label: 'Vehicle registration (Fahrzeugausweis)' },
    { value: 'purchase', label: 'Purchase contract' },
    { value: 'leasing', label: 'Leasing contract' },
    { value: 'insurance', label: 'Insurance policy' },
    { value: 'other', label: 'Other' },
];

export interface CarDocument {
    documentId: number;
    documentType: CarDocumentType;
    validFrom: string | null;
    validUntil: string | null;
    description: string | null;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    uploadedOnCarId: number;
    uploadedBy: string;
    uploadedAt: string | null;
    updatedBy: string | null;
    updatedAt: string | null;
    /** SharePoint archive copy (migration 225): the link once a copy succeeded. */
    spWebUrl: string | null;
    spCopiedAt: string | null;
    /** The last copy failure. All three null: never copied. */
    spCopyError: string | null;
}

export interface CarDocumentMetadata {
    documentType: CarDocumentType;
    validFrom: string | null;
    validUntil: string | null;
    description: string | null;
}

// Lookup types
export interface CarBrand {
    Id: number;
    BrandName: string;
    SortOrder: number;
    IsActive: boolean;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface CarFuelType {
    Id: number;
    FuelCode: string;
    FuelLabel: string;
    IsElectric: boolean;
    SortOrder: number;
    IsActive: boolean;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface CarLeasingCompany {
    Id: number;
    CompanyName: string;
    SortOrder: number;
    IsActive: boolean;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface CarInsuranceCompany {
    Id: number;
    CompanyName: string;
    SortOrder: number;
    IsActive: boolean;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

// Employee (for driver combobox)
export interface CarEmployee {
    StaffID: string;
    FullName: string;
    LastName: string;
    FirstName: string;
    EMail: string | null;
    CompanyCode: string;
    Short: string | null;
}

// Company (from etl.DolderCompany)
export interface CompanyOption {
    CompanyCode: string;
    CompanyName: string;
}

// Fixed Asset (from etl.FixedAsset)
export interface FixedAssetOption {
    FixedAssetCode: string;
    Description: string;
    CompanyCode: string;
}

// Create/update types
export interface CreateCarData {
    companyCode: string;
    staffId?: string | null;
    driverName?: string | null;
    driverShort?: string | null;
    staffNumber?: string | null;
    driverEmail?: string | null;
    carBrandId?: number | null;
    carModel?: string | null;
    licensePlate?: string | null;
    carId?: string | null;
    vin?: string | null;
    fixedAssetNo?: string | null;
    matriculationDate?: string | null;
    endOfUse?: string | null;
    fuelTypeId?: number | null;
    kwhPer100km?: number | null;
    fuelLPer100km?: number | null;
    co2WltpKgPerKm?: number | null;
    isLeasing?: boolean;
    leasingCompanyId?: number | null;
    leasingDurationMonths?: number | null;
    leasingContractKm?: number | null;
    leasingExpiryDate?: string | null;
    currentMileageKm?: number | null;
    purchasePrice?: number | null;
    leasingRate?: number | null;
    insurance?: string | null;
    insuranceCompanyId?: number | null;
    insuranceType?: string | null;
    insuranceDeductible?: number | null;
    notes?: string | null;
    // NOTE: no `isActive` here — IsActive is a computed DB column derived from EndOfUse
    // and must never be written. Active/inactive is controlled via endOfUse.
}

export interface UpdateCarData extends Partial<CreateCarData> {
    /** Take the vehicle's documents along when the plate or company changes. */
    moveDocuments?: boolean;
}

// AI Spec Lookup
export interface SpecLookupResult {
    kwhPer100km: number | null;
    fuelLPer100km: number | null;
    co2WltpKgPerKm: number | null;
    source: string | null;
}

// Sort
export type CarSortField = 'CompanyCode' | 'DriverName' | 'BrandName' | 'CarModel' | 'LicensePlate' | 'FixedAssetNo' | 'MatriculationDate' | 'FuelLabel' | 'Co2WltpKgPerKm' | 'IsLeasing' | 'IsActive' | 'PurchasePrice';
export type SortDir = 'asc' | 'desc';
